import { ActivatedRouteSnapshot, Router, Routes } from '@angular/router';
import { inject } from '@angular/core';

import { employeeOnly } from './core/guards/employee-only.guard';
import { systemAdminOnly } from './core/guards/system-admin-only.guard';

export const routes: Routes = [
  // The backend generates confirm-email-change links as /?token=... instead of
  // /auth/confirm-email-change?token=... — redirect them here. If a future flow
  // also lands on /?token=..., give it its own path rather than sharing root.
  {
    path: '',
    pathMatch: 'full',
    canActivate: [
      (route: ActivatedRouteSnapshot) => {
        const token = route.queryParamMap.get('token');
        if (token) {
          return inject(Router).createUrlTree(['/auth/confirm-email-change'], { queryParams: { token } });
        }
        return true;
      }
    ],
    loadComponent: () =>
      import('./features/home/home-page.component').then(m => m.HomePageComponent)
  },

  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES)
  },
  {
    path: 'employer',
    loadChildren: () => import('./features/employer/employer.routes').then(m => m.EMPLOYER_ROUTES)
  },
  {
    path: 'employee',
    canMatch: [employeeOnly],
    loadChildren: () => import('./features/employee/employee.routes').then(m => m.EMPLOYEE_ROUTES)
  },

  // Standalone employee activation flow — opened from the welcome email.
  {
    path: 'activate',
    loadComponent: () =>
      import('./features/auth/activation/activation-page.component')
        .then(m => m.ActivationPageComponent)
  },
  // Existing-user welcome emails link here. The activation page decides which
  // flow to render from `profile.isExistingUser`, so we just reuse the same component.
  {
    path: 'activate/existing',
    loadComponent: () =>
      import('./features/auth/activation/activation-page.component')
        .then(m => m.ActivationPageComponent)
  },

  // DEV PREVIEW of the remittance guide flow (sample data, no backend). Remove
  // once the real API-backed remittance center is wired up.
  {
    path: 'remittances-preview',
    loadComponent: () =>
      import('./features/employer/remittances/remittance-guide-preview.component')
        .then(m => m.RemittanceGuidePreviewComponent)
  },

  // Public contact page — wraps the form with the landing nav and footer.
  {
    path: 'contact',
    loadComponent: () =>
      import('./features/contact/contact-page.component')
        .then(m => m.ContactPageComponent)
  },

  // Public FAQ page — single source of truth for all FAQs (features/faq/faq.data.ts).
  {
    path: 'faq',
    loadComponent: () =>
      import('./features/faq/faq-page.component').then(m => m.FaqPageComponent)
  },

  // Public tutorials page — embedded how-to videos (features/tutorials/tutorials.data.ts).
  {
    path: 'tutorials',
    loadComponent: () =>
      import('./features/tutorials/tutorials-page.component').then(m => m.TutorialsPageComponent)
  },

  // Admin Concierge console — read-only employer impersonation. Intentionally
  // not in any nav; reached by direct URL. The impersonate API is admin-gated
  // server-side.
  {
    path: 'admin/concierge',
    loadComponent: () =>
      import('./features/admin/concierge-page.component').then(m => m.ConciergePageComponent)
  },

  // System-Admin dashboard — internal EmitRoll staff only (SystemAdmin role).
  {
    path: 'admin/dashboard',
    canMatch: [systemAdminOnly],
    loadChildren: () =>
      import('./features/admin/dashboard/admin-dashboard.routes').then(m => m.ADMIN_DASHBOARD_ROUTES)
  },

  // Static legal pages (draft stubs until final docs are prepared).
  {
    path: 'privacy',
    loadComponent: () =>
      import('./features/legal/privacy-page.component').then(m => m.PrivacyPageComponent)
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./features/legal/terms-page.component').then(m => m.TermsPageComponent)
  },

  // ---------------------------------------------------------------------------
  // Compatibility redirects for email links that were generated without the
  // `/auth/` prefix. The backend should emit `/auth/reset-password` going
  // forward; these handle links already sitting in users' inboxes.
  // ---------------------------------------------------------------------------
  {
    path: 'reset-password',
    redirectTo: ({ queryParams }) => {
      const token = queryParams['token'];
      return token
        ? `/auth/reset-password?token=${encodeURIComponent(token)}`
        : '/auth/forgot-password';
    }
  },
  { path: 'forgot-password', redirectTo: '/auth/forgot-password' },

  // wildcard - keep last
  { path: '**', redirectTo: '' }
];
